'use client';

import { c as _c } from "react/compiler-runtime";
import { Spinner } from '@common/components';
import { useToast } from '@common/hooks';
import { SvgAdd, SvgLink } from '@common/icon';
import { cn, isDev } from '@core/utils';
import { usePathname, useSearchParams } from 'next/navigation';
import { useEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { Image } from '@/components/client';
import { ResponsiveDialog } from '@/components/organism/ResponsiveDialog';
import { ENV, SEARCH_PARAMS } from '@/constants';
import { useCheckWelloApp } from '@/hooks/useCheckWelloApp';
import { useClipboard } from '@/hooks/useClipboard';
import { useKakaoShare } from '@/hooks/useKakaoShare';
import { MESSAGE_KEYWORD, useHandleApp } from '@/modules/wello-app';
import { useAuthStore } from '@/stores/AuthStore';
import { useDeviceInfoStore, WEBVIEW_TYPE } from '@/stores/DeviceStore';
import { useGlobalStore } from '@/stores/GlobalStore';
interface KakaoWindow extends Window {
  Kakao?: {
    isInitialized?: () => boolean;
    init?: (clientId: string) => void;
    Share?: {
      sendScrap?: (options: {
        requestUrl: string;
      }) => void;
    };
  };
}
export const ShareDrawer = () => {
  const [webviewType, osType] = useDeviceInfoStore(useShallow(state => [state.webviewType, state.osType]));
  const {
    isWelloApp
  } = useCheckWelloApp();
  const {
    isInitialized
  } = useKakaoShare();
  const [shareLink, setShareLink] = useGlobalStore(useShallow(state_0 => [state_0.shareLink, state_0.setShareLink]));
  const memberCode = useAuthStore(state_1 => state_1.myInfo?.memberCode);
  const shareLinkUrl = (() => {
    if (!shareLink) return;
    const pathname = new URL(shareLink).pathname;
    let mainDomain = ENV.NEXT_PUBLIC_DOMAIN;
    if (!isDev) {
      mainDomain = mainDomain.replace('https://', 'https://www.');
    }
    const url = new URL(pathname, mainDomain);
    url.searchParams.set(SEARCH_PARAMS.ENTER_TYPE, 'share');
    if (memberCode) {
      url.searchParams.set(SEARCH_PARAMS.SHARE_BY, memberCode);
    }
    return url.href;
  })();
  const copy = useClipboard();
  const {
    request: checkKakaoInstallRequest,
    response: checkKakaoInstallResponse
  } = useHandleApp({
    type: MESSAGE_KEYWORD.CHECK_KAKAO_INSTALLED
  });
  const {
    request: requestShare,
    isPending,
    response
  } = useHandleApp({
    type: MESSAGE_KEYWORD.SHARE_CONTENT
  });
  const isKakaoShareResponse = response?.params?.shareType;
  useEffect(() => {
    if (isKakaoShareResponse) {
      setShareLink(null);
    }
  }, [isKakaoShareResponse, setShareLink]);
  const {
    toast
  } = useToast();
  const isMobileWeb = !webviewType && osType && ['ios', 'android'].includes(osType);
  const isOpened = !!shareLink;
  useEffect(() => {
    if (isOpened) {
      checkKakaoInstallRequest();
    }
  }, [checkKakaoInstallRequest, isOpened]);
  const pathname_0 = usePathname();
  const searchParams = useSearchParams();
  useEffect(() => {
    setShareLink(null);
  }, [pathname_0, searchParams, setShareLink]);
  const handleKakaoClickInApp = async () => {
    if (isPending) return;
    if (checkKakaoInstallResponse?.isSuccess && shareLinkUrl) {
      requestShare({
        shareType: 'kakao',
        url: shareLinkUrl
      });
      setShareLink(null);
    } else {
      toast({
        message: '카카오톡이 설치되어 있지 않아요',
        type: 'fail'
      });
    }
  };
  const handleKakaoClickInWeb = async () => {
    if (!shareLinkUrl) return;
    const kakao = (window as KakaoWindow).Kakao;
    kakao?.Share?.sendScrap?.({
      requestUrl: shareLinkUrl
    });
    setShareLink(null);
  };
  return <ResponsiveDialog opened={!!shareLink} onClose={() => setShareLink(null)} drawerType="bottom" className={{
    modal: 'w-full max-w-[480px]'
  }}>
      <div className={cn('mx-42 flex justify-around gap-6 pb-16', {
      'justify-between': isWelloApp
    })}>
        {webviewType !== WEBVIEW_TYPE.KB_PAY ? <KakaoButton isLoading={!isInitialized} onClick={isWelloApp ? handleKakaoClickInApp : handleKakaoClickInWeb} /> : null}
        <button type="button" className="flex cursor-pointer flex-col items-center justify-center gap-6 border-none" onClick={() => {
        try {
          if (shareLinkUrl) {
            return copy({
              value: shareLinkUrl
            });
          }
        } finally {
          setShareLink(null);
        }
      }}>
          <div className="flex size-48 items-center justify-center rounded-pill bg-Gray-500 text-Gray-white">
            <SvgLink />
          </div>
          <h4 className="font-400 text-Gray-900 typo-body4">URL 복사</h4>
        </button>
        {isWelloApp || isMobileWeb ? <button type="button" className="flex cursor-pointer flex-col items-center justify-center gap-6 border-none" onClick={() => {
        try {
          if (!shareLinkUrl) return;
          if (isWelloApp) return requestShare({
            shareType: 'uni',
            url: shareLinkUrl
          });
          if (isMobileWeb) return navigator.share({
            url: shareLinkUrl
          });
        } finally {
          setShareLink(null);
        }
      }}>
            <div className="flex size-48 items-center justify-center rounded-pill bg-Gray-200 text-Gray-black">
              <SvgAdd />
            </div>
            <h4 className="font-400 text-Gray-900 typo-body4">더 보기</h4>
          </button> : null}
      </div>
    </ResponsiveDialog>;
};
interface KakaoButtonProps {
  isLoading: boolean;
  onClick: () => void;
}
const KakaoButton = t0 => {
  const $ = _c(10);
  const {
    isLoading,
    onClick
  } = t0;
  let t1;
  if ($[0] !== isLoading) {
    t1 = cn("flex cursor-pointer flex-col items-center justify-center gap-6 border-none", {
      "cursor-not-allowed opacity-50": isLoading
    });
    $[0] = isLoading;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== isLoading) {
    t2 = <div className="flex size-48 items-center justify-center rounded-pill bg-[#fbdf03]">{isLoading ? <Spinner size={24} /> : <Image alt="kakao" height={24} src="/assets/pngs/login/kakao.png" width={24} />}</div>;
    $[2] = isLoading;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <span className="font-400 text-Gray-900 typo-body4">카카오톡</span>;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== isLoading || $[6] !== onClick || $[7] !== t1 || $[8] !== t2) {
    t4 = <button type="button" disabled={isLoading} className={t1} onClick={onClick}>{t2}{t3}</button>;
    $[5] = isLoading;
    $[6] = onClick;
    $[7] = t1;
    $[8] = t2;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
};